import * as ft from 'funtypes'
import { NonEmptyString, objectWith } from '../validation.js'
import type { GeChargepoint } from './scrape.js'

/**
 * Hand-recorded corrections to individual scraped rows.
 *
 * `data/goingelectric/overrides.json` is a small file a human maintains, applied
 * by `tools/ge-voltage-windows.ts` before it decides whether a row is plausible.
 * It exists because some rows are not junk but *recoverable* junk: a CCS gun
 * recorded at 100 V with a 1000 V sibling of identical power and current on the
 * same cabinet is a dropped trailing zero, and dropping the row throws away a
 * figure we can read straight off the hardware beside it.
 *
 * Three rules make this safe to keep across a refreshed scrape, and all three
 * exist because a stale override is worse than no override - it would keep
 * substituting a value long after upstream had published a better one.
 *
 *  1. **Keyed by `chargepointId`.** That is GoingElectric's own row id, present on
 *     every row and stable across exports, unlike `evseId`, which the operator
 *     publishes and can renumber. A row whose id has gone is reported as
 *     unmatched rather than quietly ignored.
 *  2. **`from` must still match.** An override names the wrong value it was
 *     written for. If the scrape now says something else - including the right
 *     answer - the override does not apply and is reported as stale, so it can be
 *     deleted. Overrides are not allowed to win an argument with a corrected
 *     upstream.
 *  3. **Every outcome is reported.** Applied, stale and unmatched all land in
 *     `conflicts.json`. The ledger of corrections in force is generated from the
 *     data rather than trusted to be what the file says.
 */

/** One field's correction: the value we saw, and the value it should be. */
export const GeCorrection = ft.Object({
  /** The wrong value the override was recorded against. Must still match. */
  from: ft.Number,
  to: ft.Number,
})
export type GeCorrection = ft.Static<typeof GeCorrection>

export const GeOverride = objectWith(
  {
    /** GoingElectric's own chargepoint id. The key, and the only matcher. */
    chargepointId: NonEmptyString,
    /** Why this is a correction rather than an opinion. Written for a reviewer. */
    reason: NonEmptyString,
    /** `YYYY-MM-DD`, so an override nobody has revisited in two years is visible. */
    recordedAt: ft.String.withConstraint(
      (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) || 'Expected YYYY-MM-DD',
      { name: 'IsoDate' },
    ),
  },
  {
    volts: GeCorrection,
    amps: GeCorrection,
    /**
     * Where to look. Locators, deliberately *not* matchers: an override that
     * matched on a site id as well would silently stop applying when
     * GoingElectric re-slugged a station, and there would be nothing to read.
     */
    siteId: ft.String,
    url: ft.String,
    evseId: ft.String,
  },
)
export type GeOverride = ft.Static<typeof GeOverride>

/**
 * A whole scraped site struck out, because the charger it describes is not there.
 *
 * A separate mechanism from {@link GeOverride} because it answers a different
 * question. An override says "this number is a typo"; a retirement says "this
 * listing is not a place". GoingElectric keeps serving a page for a station long
 * after it is gone - `104711` in Skawina still returns 200 with a normal title and
 * a full connector table - and nothing in the scrape, the sitemap or the HTML says
 * otherwise. Only somebody who knows the site can say, which is why this is a
 * hand-maintained file and not a rule.
 *
 * It matters because a dead listing is not merely inert. It shares an address with
 * whatever *is* there, and its declarations then contradict the live station's, so
 * the live station loses its address match to a charger that no longer exists.
 *
 * `chargepoints` is the guard, and it plays the same part `from` plays on an
 * override: a retirement is a claim about a specific observed listing, so if the
 * next scrape shows a different number of connectors the claim needs looking at
 * again rather than applying to whatever the page has become.
 */
export const GeRetirement = objectWith(
  {
    /** GoingElectric's own site id, from the page URL. */
    siteId: NonEmptyString,
    /** Why this listing is not a place. Written for whoever reviews it in a year. */
    reason: NonEmptyString,
    recordedAt: ft.String.withConstraint(
      (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) || 'Expected YYYY-MM-DD',
      { name: 'IsoDate' },
    ),
  },
  {
    /** Connectors the listing had when it was retired. Re-checked every run. */
    chargepoints: ft.Number,
    url: ft.String,
  },
)
export type GeRetirement = ft.Static<typeof GeRetirement>

export const GeOverridesFile = objectWith(
  {
    overrides: ft.Array(GeOverride),
  },
  {
    /** Whole listings struck out. Optional: a repository may have none. */
    retired: ft.Array(GeRetirement),
  },
)
export type GeOverridesFile = ft.Static<typeof GeOverridesFile>

/** Which fields an override may correct. Both are electrical, both are numbers. */
export const GE_OVERRIDE_FIELDS = ['volts', 'amps'] as const
export type GeOverrideField = (typeof GE_OVERRIDE_FIELDS)[number]

/** One field whose `from` no longer describes the scrape. Delete the override. */
export interface GeStaleCorrection {
  readonly field: GeOverrideField
  /** What the override expected to find. */
  readonly expected: number
  /** What the scrape says now. `null` when the field has gone empty. */
  readonly actual: number | null
}

export interface GeOverrideResult {
  /** The row, corrected where an override applied. The original object if none did. */
  readonly chargepoint: GeChargepoint
  readonly applied: GeOverrideField[]
  readonly stale: GeStaleCorrection[]
}

/**
 * Apply one override to one row.
 *
 * Field by field, and each field independently: an override that corrects both
 * `volts` and `amps` may find one still wrong and the other already fixed, and
 * the half that still applies should apply. Returns the row untouched when
 * nothing matched, so a caller can compare by identity.
 */
export const applyGeOverride = (
  chargepoint: GeChargepoint,
  override: GeOverride | undefined,
): GeOverrideResult => {
  if (override === undefined) return { chargepoint, applied: [], stale: [] }

  const applied: GeOverrideField[] = []
  const stale: GeStaleCorrection[] = []
  const corrected: { volts?: number; amps?: number } = {}

  GE_OVERRIDE_FIELDS.forEach((field) => {
    const correction = override[field]
    if (correction === undefined) return

    const actual = chargepoint[field]
    if (actual === correction.from) {
      corrected[field] = correction.to
      applied.push(field)
      return
    }
    // Includes the happy case: upstream published `to` itself, so the override
    // has done its job and should now go. Reported the same way as any other
    // mismatch, because either way the file no longer describes the data.
    stale.push({ field, expected: correction.from, actual })
  })

  if (applied.length === 0) return { chargepoint, applied, stale }
  return { chargepoint: { ...chargepoint, ...corrected }, applied, stale }
}
